/**
 * Building blocks for the tax pages in the Property Portfolio layout
 * (Fan, 2026-10-27): KPI cards, year switcher, All/France/UK pills,
 * a monthly stacked bar chart and a plain monthly table.
 * Display only: callers pass figures already in the display currency.
 */
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ComposedChart, Bar, Line, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { formatCompact } from '../../lib/chartFormatters';

const SYM = { GBP: '\u00a3', EUR: '\u20ac', DKK: 'kr ', USD: '$' };

export function fmtMoney(v, ccy = 'GBP', { decimals = 2 } = {}) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
  const n = Number(v);
  const sym = SYM[ccy] ?? `${ccy} `;
  return `${n < 0 ? '-' : ''}${sym}${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

export function fmtShort(v, ccy = 'GBP') {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
  return formatCompact(Number(v), ccy) || fmtMoney(v, ccy, { decimals: 0 });
}

export function KpiCard({ label, value, subDetail, tooltip, tone, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-1 px-4 pt-4">
        <CardTitle className="text-xs font-medium text-muted-foreground truncate" title={tooltip || label}>{label}</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className={cn('text-xl font-bold font-mono leading-tight', tone === 'bad' && 'text-red-600', tone === 'good' && 'text-emerald-600')}>{value}</div>
        {subDetail && <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{subDetail}</p>}
      </CardContent>
    </Card>
  );
}

export function YearNav({ year, setYear }) {
  const thisYear = new Date().getFullYear();
  return (
    <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-card">
      <button type="button" onClick={() => setYear((y) => y - 1)} className="p-1 rounded hover:bg-muted transition-colors" aria-label="Previous year">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="font-semibold w-12 text-center" data-testid="tax-year">{year}</span>
      <button
        type="button"
        onClick={() => setYear((y) => y + 1)}
        disabled={year >= thisYear}
        className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Next year"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export function PillTabs({ value, onChange, options, testId }) {
  return (
    <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit" data-testid={testId}>
      {options.map((t) => (
        <button
          type="button"
          key={t.key}
          className={cn(
            'px-5 py-2 text-sm font-semibold rounded-md transition-all',
            value === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export const REGION_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'fr', label: '\uD83C\uDDEB\uD83C\uDDF7 France' },
  { key: 'uk', label: '\uD83C\uDDEC\uD83C\uDDE7 UK' },
];

export function SectionCard({ title, subtitle, right, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {right}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload, label, ccy, series }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.dataKey !== '_labelY' && Math.abs(Number(p.value) || 0) > 0.005);
  const total = rows.reduce((s, p) => s + (Number(p.value) || 0), 0);
  return (
    <div className="rounded-md border bg-background px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold mb-1">{label}</div>
      {rows.map((p) => {
        const s = series.find((x) => x.key === p.dataKey);
        return (
          <div key={p.dataKey} className="flex justify-between gap-4">
            <span style={{ color: p.color }}>{s?.label || p.dataKey}</span>
            <span className="font-mono">{fmtMoney(p.value, ccy)}</span>
          </div>
        );
      })}
      {rows.length > 1 && (
        <div className="flex justify-between gap-4 border-t mt-1 pt-1 font-semibold">
          <span>Net</span><span className="font-mono">{fmtMoney(total, ccy)}</span>
        </div>
      )}
    </div>
  );
}

function segmentValueLabel(ccy) {
  return function SegmentValueLabel(lp) {
    const { x, y, width, height, value } = lp;
    if (value == null || value === 0) return null;
    const h = Math.abs(height || 0);
    if (h < 14 || (width || 0) < 26) return null;
    const top = Math.min(y, y + (height || 0));
    return (
      <text x={x + width / 2} y={top + h / 2} textAnchor="middle" dominantBaseline="central"
        fontSize={10} fontWeight={600} fill="#fff">
        {fmtShort(value, ccy)}
      </text>
    );
  };
}

/**
 * Stacked monthly bars with numbers on them (Fan, 2026-10-28: "the charts
 * have no numbers"): each segment big enough carries its value, and the
 * month total sits above the bar on an invisible unstacked Line (a
 * zero-height anchor bar drops to 0 under sign stacking, PR #241).
 */
export function MonthlyStackedChart({ data, series, ccy, height = 280, testId }) {
  const rows = (data || []).map((r) => {
    let pos = 0;
    let net = 0;
    let any = false;
    series.forEach((s) => {
      const v = Number(r[s.key]) || 0;
      if (v) any = true;
      net += v;
      if (v > 0) pos += v;
    });
    return { ...r, _total: any ? net : null, _labelY: any ? pos : null };
  });
  return (
    <div style={{ width: '100%', height }} data-testid={testId}>
      <ResponsiveContainer>
        <ComposedChart data={rows} stackOffset="sign" margin={{ top: 22, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtShort(v, ccy)} width={60} />
          <Tooltip content={<ChartTooltip ccy={ccy} series={series} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <ReferenceLine y={0} stroke="#9CA3AF" />
          {series.map((s) => (
            <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={s.color} isAnimationActive={false}>
              <LabelList dataKey={s.key} content={segmentValueLabel(ccy)} />
            </Bar>
          ))}
          <Line dataKey="_labelY" stroke="none" dot={false} activeDot={false} legendType="none"
            isAnimationActive={false} tooltipType="none">
            <LabelList
              content={(lp) => {
                const row = rows[lp.index];
                if (!row || row._total == null || Math.abs(row._total) < 0.5) return null;
                return (
                  <text x={lp.x} y={lp.y - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#111827">
                    {fmtShort(row._total, ccy)}
                  </text>
                );
              }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
